import React, { forwardRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Heart, MessageCircle, Globe, Plane, Zap, LucideIcon } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";

interface NavTabProps {
  path: string;
  icon: LucideIcon;
  label: string;
  badge?: number;
  isActive: boolean;
}

const NavTab = forwardRef<HTMLAnchorElement, NavTabProps>(
  ({ path, icon: Icon, label, badge, isActive }, ref) => {
    return (
      <NavLink
        ref={ref}
        to={path}
        className="relative flex flex-col items-center gap-1 px-3 py-2 group"
      >
        <div className={cn(
          "relative transition-all duration-300 ease-out",
          isActive ? "text-pop scale-110" : "text-muted-foreground group-hover:text-foreground group-hover:scale-105"
        )}>
          <Icon className={cn(
            "w-6 h-6 transition-all duration-300",
            isActive && "fill-pop/20 drop-shadow-sm"
          )} />
          {badge ? (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-pop text-white text-[10px] flex items-center justify-center font-bold shadow-sm animate-scale-up">
              {badge > 9 ? "9+" : badge}
            </span>
          ) : null}
        </div>
        <span className={cn(
          "text-[10px] font-medium transition-all duration-300",
          isActive ? "text-pop" : "text-muted-foreground group-hover:text-foreground"
        )}>
          {label}
        </span>
        {/* Active indicator dot */}
        <div className={cn(
          "absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-pop transition-all duration-300",
          isActive ? "opacity-100 scale-100" : "opacity-0 scale-0"
        )} />
      </NavLink>
    );
  }
);

NavTab.displayName = "NavTab";

const BottomNav = forwardRef<HTMLElement>((_, ref) => {
  const location = useLocation();
  const { unreadCount } = useNotifications();

  const tabs = [
    { path: "/", icon: Heart, label: "Pop Balloon" },
    { path: "/global-connect", icon: Plane, label: "Global Connect" },
    { path: "/love-exhibition", icon: Globe, label: "Live Dating Event" },
    { path: "/portal", icon: Zap, label: "My Portal" },
    { path: "/matches", icon: MessageCircle, label: "My Matches", badge: unreadCount },
  ];

  return (
    <nav ref={ref} className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-xl border-t border-border/50 z-40 shadow-lg">
      <div className="flex items-center justify-around py-1.5 max-w-lg mx-auto safe-area-pb">
        {tabs.map((tab) => {
          const isActive = location.pathname === tab.path;
          return (
            <NavTab
              key={tab.path}
              path={tab.path}
              icon={tab.icon}
              label={tab.label}
              badge={tab.badge}
              isActive={isActive}
            />
          );
        })}
      </div>
    </nav>
  );
});

BottomNav.displayName = "BottomNav";

export default BottomNav;
